import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { privFor } from "@/lib/priv";
import PrivilegeUsers from "@/pages/PrivilegeUsers";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import OwnerDashboard from "@/pages/OwnerDashboard";
import SmartMenu from "@/pages/SmartMenu";
import DashboardHarian from "@/pages/DashboardHarian";
import LiveProductionBoard from "@/pages/LiveProductionBoard";
import ProductionSchedule from "@/pages/ProductionSchedule";
import UnitTracking from "@/pages/UnitTracking";
import MyProfile from "@/pages/MyProfile";
import ClaimActive from "@/pages/ClaimActive";
import ClaimDetail from "@/pages/ClaimDetail";
import Workshop from "@/pages/Workshop";
import Billing from "@/pages/Billing";
import History from "@/pages/History";
import PhaseQueue from "@/pages/PhaseQueue";
import POPartman from "@/pages/POPartman";
import WarehouseParts from "@/pages/WarehouseParts";
import WarehouseVehicle from "@/pages/WarehouseVehicle";
import QuickRegister from "@/pages/QuickRegister";
import WaitingSpk from "@/pages/WaitingSpk";
import PartsMonitoring from "@/pages/PartsMonitoring";
import ReconDashboard from "@/pages/ReconDashboard";
import WarehouseSalvage from "@/pages/WarehouseSalvage";
import WarehouseMaterial from "@/pages/WarehouseMaterial";
import WarehousePaint from "@/pages/WarehousePaint";
import Finance from "@/pages/Finance";
import MasterData from "@/pages/MasterData";
import CashBank from "@/pages/CashBank";
import PettyCash from "@/pages/PettyCash";
import AccountsPayable from "@/pages/AccountsPayable";
import DailyCashBook from "@/pages/DailyCashBook";
import Estimates from "@/pages/Estimates";
import Accounting from "@/pages/Accounting";
import SAReport from "@/pages/SAReport";
import TechnicianWorklist from "@/pages/TechnicianWorklist";
import KPIPerformance from "@/pages/KPIPerformance";
import ReportsCenter from "@/pages/ReportsCenter";
import ProductionFloor from "@/pages/ProductionFloor";
import Kalender from "@/pages/Kalender";
import KpiTim from "@/pages/KpiTim";
import Karyawan from "@/pages/Karyawan";
import PekerjaanHarian from "@/pages/PekerjaanHarian";
import RiwayatPekerjaan from "@/pages/RiwayatPekerjaan";
import RootSetup from "@/pages/RootSetup";
import OwnerSecurity from "@/pages/OwnerSecurity";
import OwnerBootstrap from "@/pages/OwnerBootstrap";
import OwnerCommand from "@/pages/OwnerCommand";
import SecurityPos from "@/pages/SecurityPos";
import SecurityMovement from "@/pages/SecurityMovement";
import SecurityLog from "@/pages/SecurityLog";
import SecurityRecon from "@/pages/SecurityRecon";
import SecurityReports from "@/pages/SecurityReports";
import SecurityDashboard from "@/pages/SecurityDashboard";
import OrPayments from "@/pages/OrPayments";
import FinanceDashboard from "@/pages/FinanceDashboard";
import ChangePasswordFirst from "@/pages/ChangePasswordFirst";
import WarRoom from "@/pages/WarRoom";
import ReportControl from "@/pages/ReportControl";
import FinanceHub from "@/pages/FinanceHub";
import ApPayRequests from "@/pages/ApPayRequests";
import FinClosing from "@/pages/FinClosing";
import FinAuditLog from "@/pages/FinAuditLog";
import { FinCoa, FinCashFlow } from "@/pages/FinCoaCashFlow";
import CashRequests from "@/pages/CashRequests";
import { FIN_CONFIDENTIAL_ROLES } from "@/lib/finRole";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ChangePasswordFirst />;
  return children;
}

const FINANCE_ROLES = ["Owner", "Finance Manager", "Admin Finance", "Controller"];

function usePrivGate() {
  const { user } = useAuth();
  const loc = useLocation();
  return privFor(user, loc.pathname);
}

function FinRoute({ allow = FINANCE_ROLES, children }) {
  const { user } = useAuth();
  const pv = usePrivGate();
  const ok = pv !== "DENY" && user && (pv === "ALLOW" || user.role === "root" || allow.includes(user.role_name));
  if (!ok) return <Navigate to="/" replace />;
  return children;
}
function RoleRoute({ allow, children }) {
  const { user } = useAuth();
  const pv = usePrivGate();
  const ok = pv !== "DENY" && user && (pv === "ALLOW" || user.role === "admin" || user.role === "root" || allow.includes(user.role_name));
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

function RootRoute({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== "root") return <Navigate to="/" replace />;
  return children;
}

function BlockRoute({ block, children }) {
  const { user } = useAuth();
  const pv = usePrivGate();
  if (pv === "DENY") return <Navigate to="/" replace />;
  if (pv !== "ALLOW" && user && user.role !== "admin" && user.role !== "root" && block.includes(user.role_name)) return <Navigate to="/" replace />;
  return children;
}

function RoleIndex() {
  const { user } = useAuth();
  const r = user?.role_name;
  const mgmt = user?.role === "admin" || user?.role === "root" || ["Owner", "Admin", "GM", "Branch Manager", "Controller", "Business Manager"].includes(r);
  if (mgmt) return <OwnerDashboard />;
  if (["Finance Manager", "Admin Finance"].includes(r)) return <Navigate to="/finance-dashboard" replace />;
  if (["Service Advisor", "SA_CS", "Customer Service"].includes(r)) return <Navigate to="/sa-report" replace />;
  if (["Kepala Bengkel", "KBG2"].includes(r)) return <Navigate to="/dashboard-harian" replace />;
  if (["Kepala Produksi", "Foreman", "QC", "Production"].includes(r)) return <Navigate to="/live-production" replace />;
  if (["ESTIMATOR_PARTMAN", "Partman", "Purchasing"].includes(r)) return <Navigate to="/po-partman" replace />;
  if (r === "Warehouse") return <Navigate to="/warehouse/parts" replace />;
  if (r === "HR GA") return <Navigate to="/karyawan" replace />;
  if (user?.role_name === "Security") return <Navigate to="/security/dashboard" replace />;
  return <Navigate to="/my-jobs" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="bottom-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/root-setup" element={<RootSetup />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<RoleIndex />} />
              <Route path="/menu" element={<SmartMenu />} />
              <Route path="/dashboard-harian" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "KBG2", "Service Advisor", "SA_CS", "Finance", "Controller", "Business Manager"]}><DashboardHarian /></RoleRoute>} />
              <Route path="/sa-report" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Service Advisor", "SA_CS", "Customer Service", "Controller"]}><SAReport /></RoleRoute>} />
              <Route path="/dashboard-classic" element={<Dashboard />} />
              <Route path="/live-production" element={<LiveProductionBoard />} />
              <Route path="/production-schedule" element={<ProductionSchedule />} />
              <Route path="/tracking" element={<UnitTracking />} />
              <Route path="/tracking/:id" element={<UnitTracking />} />
              <Route path="/my-jobs" element={<TechnicianWorklist />} />
              <Route path="/kpi" element={<KPIPerformance />} />
              <Route path="/reports" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Finance", "Service Advisor", "SA_CS", "Controller", "Business Manager", "Kepala Produksi"]}><ReportsCenter /></RoleRoute>} />
              <Route path="/production-floor" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "QC", "Kepala Bengkel", "KBG2", "Kepala Produksi", "Foreman", "Controller"]}><ProductionFloor /></RoleRoute>} />
              <Route path="/kalender" element={<Kalender />} />
              <Route path="/kalender/:kind" element={<Kalender />} />
              <Route path="/kpi-tim" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "KBG2", "Kepala Produksi", "Foreman", "Production", "Controller", "Business Manager"]}><KpiTim /></RoleRoute>} />
              <Route path="/karyawan" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "KBG2", "Kepala Produksi", "Foreman", "HR GA", "Controller"]}><Karyawan /></RoleRoute>} />
              <Route path="/pekerjaan-harian" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Kepala Produksi", "Foreman", "Production"]}><PekerjaanHarian /></RoleRoute>} />
              <Route path="/riwayat-pekerjaan" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Kepala Produksi", "Foreman"]}><RiwayatPekerjaan /></RoleRoute>} />
              <Route path="/profile" element={<MyProfile />} />
              <Route path="/pre-order" element={<BlockRoute block={["Partman", "Warehouse", "ESTIMATOR_PARTMAN"]}><PhaseQueue phase="PRE_ORDER" /></BlockRoute>} />
              <Route path="/estimates" element={<BlockRoute block={["Partman", "Warehouse"]}><Estimates /></BlockRoute>} />
              <Route path="/repair-order" element={<PhaseQueue phase="REPAIR_ORDER" />} />
              <Route path="/finish-order" element={<PhaseQueue phase="FINISH_ORDER" />} />
              <Route path="/po-partman" element={<POPartman />} />
              <Route path="/warehouse/parts" element={<WarehouseParts />} />
              <Route path="/warehouse/parts/:plate" element={<WarehouseVehicle />} />
              <Route path="/claims/register" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Service Advisor", "SA_CS", "Customer Service", "Controller"]}><QuickRegister /></RoleRoute>} />
              <Route path="/waiting-spk" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Service Advisor", "SA_CS", "Customer Service", "Controller"]}><WaitingSpk /></RoleRoute>} />
              <Route path="/parts-monitoring" element={<PartsMonitoring />} />
              <Route path="/recon-dashboard" element={<FinRoute allow={[...FINANCE_ROLES, "Business Manager"]}><ReconDashboard /></FinRoute>} />
              <Route path="/warehouse/material" element={<WarehouseMaterial />} />
              <Route path="/warehouse/paint" element={<WarehousePaint />} />
              <Route path="/warehouse/salvage" element={<WarehouseSalvage />} />
              <Route path="/finance" element={<FinRoute><Finance /></FinRoute>} />
              <Route path="/cash-bank" element={<FinRoute><CashBank /></FinRoute>} />
              <Route path="/petty-cash" element={<FinRoute><PettyCash /></FinRoute>} />
              <Route path="/accounts-payable" element={<FinRoute><AccountsPayable /></FinRoute>} />
              <Route path="/daybook" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><DailyCashBook /></FinRoute>} />
              <Route path="/accounting" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><Accounting /></FinRoute>} />
              <Route path="/fin/kas-bank" element={<FinRoute><FinanceHub section="kas-bank" /></FinRoute>} />
              <Route path="/fin/piutang-utang" element={<FinRoute allow={[...FINANCE_ROLES, "Business Manager"]}><FinanceHub section="piutang-utang" /></FinRoute>} />
              <Route path="/fin/laporan-kontrol" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><FinanceHub section="laporan-kontrol" /></FinRoute>} />
              <Route path="/fin/kas-keluar" element={<FinRoute><CashRequests /></FinRoute>} />
              <Route path="/fin/approval-pembayaran" element={<FinRoute><ApPayRequests /></FinRoute>} />
              <Route path="/fin/closing" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><FinClosing /></FinRoute>} />
              <Route path="/fin/audit-log" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><FinAuditLog /></FinRoute>} />
              <Route path="/fin/coa" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><FinCoa /></FinRoute>} />
              <Route path="/fin/cash-flow" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><FinCashFlow /></FinRoute>} />
              <Route path="/claims" element={<BlockRoute block={["Partman", "Warehouse", "ESTIMATOR_PARTMAN"]}><ClaimActive /></BlockRoute>} />
              <Route path="/claims/:id" element={<BlockRoute block={["Partman", "Warehouse", "ESTIMATOR_PARTMAN"]}><ClaimDetail /></BlockRoute>} />
              <Route path="/workshop" element={<Workshop />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/history" element={<History />} />
              <Route path="/master-data/privileges" element={<RoleRoute allow={["Owner"]}><PrivilegeUsers /></RoleRoute>} />
              <Route path="/master-data" element={<RoleRoute allow={["Owner", "Admin"]}><MasterData /></RoleRoute>} />
              <Route path="/owner-bootstrap" element={<RoleRoute allow={["Owner", "Admin"]}><OwnerBootstrap /></RoleRoute>} />
              <Route path="/owner/security" element={<RootRoute><OwnerSecurity /></RootRoute>} />
              <Route path="/owner/ai" element={<RootRoute><OwnerCommand /></RootRoute>} />
              <Route path="/security/pos" element={<RoleRoute allow={["Security", "Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager"]}><SecurityPos /></RoleRoute>} />
              <Route path="/security/masuk" element={<RoleRoute allow={["Security", "Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager"]}><SecurityMovement type="IN" /></RoleRoute>} />
              <Route path="/security/keluar" element={<RoleRoute allow={["Security", "Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager"]}><SecurityMovement type="OUT" /></RoleRoute>} />
              <Route path="/security/log" element={<RoleRoute allow={["Security", "Service Advisor", "Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager", "Controller"]}><SecurityLog /></RoleRoute>} />
              <Route path="/security/recon" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager", "Controller"]}><SecurityRecon /></RoleRoute>} />
              <Route path="/security/reports" element={<RoleRoute allow={["Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager", "Controller"]}><SecurityReports /></RoleRoute>} />
              <Route path="/security/dashboard" element={<RoleRoute allow={["Security", "Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager"]}><SecurityDashboard /></RoleRoute>} />
              <Route path="/or-payments" element={<RoleRoute allow={["Service Advisor", "Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager", "Finance", "Finance Manager", "Admin Finance", "SA_CS", "Controller"]}><OrPayments /></RoleRoute>} />
              <Route path="/finance-dashboard" element={<FinRoute allow={[...FINANCE_ROLES, "Business Manager"]}><FinanceDashboard /></FinRoute>} />
              <Route path="/report-control" element={<FinRoute allow={FIN_CONFIDENTIAL_ROLES}><ReportControl /></FinRoute>} />
              <Route path="/war-room" element={<WarRoom />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
